// The leap: a special attack where the gorilla throws himself into the air and
// lets the banana go from the top of the jump, at a point fixed before he left
// the ground.
//
// Timed in frames rather than in milliseconds, on purpose. The banana is released
// on one exact frame of the jump and the flight is solved in frames, so the frame
// loop that steps the banana steps this too. A jump measured against the wall
// clock would let the throw go at a different height on a machine that is running
// a little slow, and the whole point is that it leaves the hand at the top.

import { state } from './state.js';
import { LEAP_AIR_FRAMES, LEAP_CROUCH_FRAMES, LEAP_DIP, LEAP_HEIGHT } from './config.js';

/** The frame the banana leaves the hand on: the top of the jump. */
export const LEAP_RELEASE_FRAME = LEAP_CROUCH_FRAMES + Math.round(LEAP_AIR_FRAMES / 2);

/** The whole jump, crouch included. */
export const LEAP_TOTAL_FRAMES = LEAP_CROUCH_FRAMES + LEAP_AIR_FRAMES;

/**
 * Starts a leap. `target` is where the throw is going, worked out once here and
 * never looked at again — which is the whole difference between this and a homing
 * missile, and the reason it can miss.
 */
export function startLeap(idx, target) {
  state.leap = { idx, frames: 0, target };
}

/**
 * How far off the ground the gorilla is right now, in px. Zero for everybody else,
 * which is what makes it safe to add this to a pose that is drawn every frame.
 */
export function leapOffset(idx) {
  const l = state.leap;
  if (!l || l.idx !== idx) return 0;

  const f = l.frames;

  // Down first. A jump with no crouch reads as being yanked up on a string.
  if (f < LEAP_CROUCH_FRAMES) return -LEAP_DIP * (f / LEAP_CROUCH_FRAMES);

  const air = (f - LEAP_CROUCH_FRAMES) / LEAP_AIR_FRAMES;
  if (air >= 1) return 0;

  // A sine across the airborne half: quick off the ground, hanging at the top,
  // back down at the end, and no corner in the middle where a piecewise hop would
  // put one.
  return LEAP_HEIGHT * Math.sin(Math.PI * air);
}

export function leapDone() {
  return !!state.leap && state.leap.frames >= LEAP_TOTAL_FRAMES;
}

export function endLeap() {
  state.leap = null;
}
